import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { products } from '../data/products';
import type { Product } from '../data/products';
import { lineTotalCents, toEuros, unitPriceCentsForQty } from '../lib/pricing';

export interface CartLine {
  id: string;
  product: Product;
  qty: number;
}

const byId = new Map(products.map((product) => [product.id, product]));

/**
 * El carrito se guarda en localStorage con el producto entero dentro, así que un
 * carrito abandonado antes de un cambio de tarifa arrastraría el precio viejo y el
 * cliente vería un importe distinto del que cobra Stripe. El catálogo actual manda.
 */
function currentProduct(line: CartLine): Product {
  return byId.get(line.id) ?? line.product;
}

function baseCents(line: CartLine): number {
  return Math.round((currentProduct(line).price ?? 0) * 100);
}

/** Precio por unidad de la línea, ya con el descuento por volumen de su cantidad. */
export function lineUnitPrice(line: CartLine): number {
  return toEuros(unitPriceCentsForQty(baseCents(line), line.qty));
}

/** Importe de la línea completa. */
export function lineTotal(line: CartLine): number {
  return toEuros(lineTotalCents(baseCents(line), line.qty));
}

interface CartState {
  lines: CartLine[];
  isOpen: boolean;
  add: (product: Product, qty?: number) => void;
  remove: (id: string) => void;
  setQty: (id: string, qty: number) => void;
  clear: () => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  totalItems: () => number;
  subtotal: () => number;
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],
      isOpen: false,
      add: (product, qty = 1) =>
        set((state) => {
          if (product.availableForSale === false || product.price === undefined) return state;
          const existing = state.lines.find((l) => l.id === product.id);
          if (existing) {
            return {
              lines: state.lines.map((l) =>
                l.id === product.id ? { ...l, qty: l.qty + qty } : l,
              ),
              isOpen: true,
            };
          }
          return { lines: [...state.lines, { id: product.id, product, qty }], isOpen: true };
        }),
      remove: (id) => set((state) => ({ lines: state.lines.filter((l) => l.id !== id) })),
      setQty: (id, qty) =>
        set((state) => ({
          lines: state.lines
            .map((l) => (l.id === id ? { ...l, qty: Math.max(0, qty) } : l))
            .filter((l) => l.qty > 0),
        })),
      clear: () => set({ lines: [] }),
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set((s) => ({ isOpen: !s.isOpen })),
      totalItems: () => get().lines.reduce((acc, l) => acc + l.qty, 0),
      subtotal: () => get().lines.reduce((acc, l) => acc + lineTotal(l), 0),
    }),
    {
      name: 'vapsilver-cart',
      partialize: (s) => ({ lines: s.lines }),
    },
  ),
);
